import React, { useState, useEffect } from 'react';
import { getStrategies, toggleStrategy, updateStrategyConfig } from '../../api/client';

// ── Option universe ───────────────────────────────────────────────────────────
const INDEX_OPTIONS = [
  { label: 'NIFTY 50',  index: 'NIFTY',     symbol: 'NSE:NIFTY50-INDEX',   step: 50,  exchange: 'NSE' },
  { label: 'BANKNIFTY', index: 'BANKNIFTY',  symbol: 'NSE:NIFTYBANK-INDEX', step: 100, exchange: 'NSE' },
  { label: 'SENSEX',    index: 'SENSEX',     symbol: 'BSE:SENSEX-INDEX',    step: 100, exchange: 'BSE' },
];

const STRIKE_OPTIONS = [
  { label: 'ATM',   value: 0 },
  { label: 'OTM 1', value: 1 },
  { label: 'OTM 2', value: 2 },
  { label: 'OTM 3', value: 3 },
  { label: 'OTM 4', value: 4 },
  { label: 'OTM 5', value: 5 },
  { label: 'ITM 1', value: -1 },
  { label: 'ITM 2', value: -2 },
  { label: 'ITM 3', value: -3 },
  { label: 'ITM 4', value: -4 },
  { label: 'ITM 5', value: -5 },
];

const DESCRIPTIONS = {
  orb_15min:             '15-min Opening Range Breakout — buys CE/PE on breakout from first candle high/low.',
  orb_options:           '5-min Opening Range Breakout for options — tighter SL, VIX gate.',
  short_straddle:        'Delta-neutral short straddle/strangle — profits from time decay on weekly expiry.',
  mean_reversion:        'RSI + Bollinger Bands — enters on overbought/oversold conditions.',
  trend_following:       'EMA 20/50 crossover + RSI 50 crossover — follows momentum with trailing SL.',
  scalping_5min:         'RSI + VWAP scalping — tight targets in two intraday windows (09:30–11:30 & 14:00–15:10).',
  breakout_retest:       '30-min ORB breakout-retest-reject pattern — waits for pullback to ORB level before entering.',
  candle_breakout_15min: '15-min candle breakout with quality filters — body ratio, EMA trend, VWAP, volume spike.',
};

// ── SL mode toggle (% vs pts) ─────────────────────────────────────────────────
// These are the pct/pts field pairs; only one set is shown at a time.
const SL_PCT_FIELDS = new Set(['sl_pct', 'target_pct', 'trailing_sl_pct']);
const SL_PTS_FIELDS = new Set(['sl_pts', 'target_pts', 'trailing_sl_pts']);

// ── Config field renderer ─────────────────────────────────────────────────────
function ConfigField({ fieldKey, value, onChange }) {
  const inputStyle = { width: '100%', boxSizing: 'border-box' };
  const label = fieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Index dropdown — updates symbol automatically
  if (fieldKey === 'index') {
    return (
      <div>
        <label style={{ color: 'var(--muted)', fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</label>
        <select className="input" style={inputStyle} value={value}
          onChange={e => onChange(fieldKey, e.target.value)}>
          {INDEX_OPTIONS.map(o => (
            <option key={o.index} value={o.index}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }

  // Symbol dropdown
  if (fieldKey === 'symbol') {
    return (
      <div>
        <label style={{ color: 'var(--muted)', fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</label>
        <select className="input" style={inputStyle} value={value}
          onChange={e => onChange(fieldKey, e.target.value)}>
          {INDEX_OPTIONS.map(o => (
            <option key={o.symbol} value={o.symbol}>{o.symbol}</option>
          ))}
        </select>
      </div>
    );
  }

  // Strike / OTM offset dropdown
  if (fieldKey === 'strike_offset' || fieldKey === 'otm_offset') {
    const numVal = parseInt(value, 10) || 0;
    return (
      <div>
        <label style={{ color: 'var(--muted)', fontSize: 11, display: 'block', marginBottom: 4 }}>
          Strike Selection
        </label>
        <select className="input" style={inputStyle}
          value={numVal}
          onChange={e => onChange(fieldKey, parseInt(e.target.value, 10))}>
          {STRIKE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 3 }}>
          {numVal === 0 ? 'At The Money'
            : numVal > 0 ? `Out of The Money (+${numVal} strikes)`
            : `In The Money (${numVal} strikes)`}
        </div>
      </div>
    );
  }

  // SL mode toggle: % Percent ↔ ₹ Points
  if (fieldKey === 'sl_mode') {
    return (
      <div style={{ gridColumn: 'span 2' }}>
        <label style={{ color: 'var(--muted)', fontSize: 11, display: 'block', marginBottom: 6, fontWeight: 600 }}>
          SL / Target Mode
        </label>
        <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 7, padding: 3, border: '1px solid var(--border)', gap: 3 }}>
          {[
            { val: 'pct', icon: '%', text: 'Percentage' },
            { val: 'pts', icon: '₹', text: 'Fixed Points' },
          ].map(({ val, icon, text }) => (
            <button
              key={val}
              type="button"
              onClick={() => onChange(fieldKey, val)}
              style={{
                flex: 1, padding: '6px 0', borderRadius: 5, border: 'none', cursor: 'pointer',
                background: value === val ? 'var(--accent)' : 'transparent',
                color: value === val ? '#fff' : 'var(--muted)',
                fontWeight: 700, fontSize: 12, transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}
            >
              <span style={{ fontSize: 14 }}>{icon}</span> {text}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
          {value === 'pts'
            ? 'SL/Target fixed in ₹ points — loss & profit stay constant regardless of premium'
            : 'SL/Target as % of entry price — scales with premium value'}
        </div>
      </div>
    );
  }

  // Boolean toggle
  if (typeof value === 'boolean') {
    return (
      <div>
        <label style={{ color: 'var(--muted)', fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</label>
        <label className="toggle">
          <input type="checkbox" checked={value} onChange={e => onChange(fieldKey, e.target.checked)} />
          <span className="toggle-slider" />
        </label>
      </div>
    );
  }

  // Default: text/number input
  const isNum = typeof value === 'number';
  return (
    <div>
      <label style={{ color: 'var(--muted)', fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        className="input"
        style={inputStyle}
        type={isNum ? 'number' : 'text'}
        value={value}
        onChange={e => {
          if (isNum) {
            const parsed = parseFloat(e.target.value);
            onChange(fieldKey, isNaN(parsed) ? value : parsed);
          } else {
            onChange(fieldKey, e.target.value);
          }
        }}
      />
    </div>
  );
}

// ── Strategy card ─────────────────────────────────────────────────────────────
function StrategyCard({ strategy, onToggle, onConfigSave }) {
  const [expanded, setExpanded] = useState(false);
  const [config,   setConfig]   = useState({ ...strategy.parameters });
  const [saving,   setSaving]   = useState(false);

  const statusColor = { RUNNING: 'badge-green', WAITING: 'badge-yellow', DISABLED: 'badge-grey' };

  const handleFieldChange = (key, val) => {
    setConfig(prev => {
      const next = { ...prev, [key]: val };

      // When index changes, auto-update the linked symbol
      if (key === 'index') {
        const match = INDEX_OPTIONS.find(o => o.index === val);
        if (match && 'symbol' in next) next.symbol = match.symbol;
      }
      // When symbol changes, auto-update the linked index
      if (key === 'symbol') {
        const match = INDEX_OPTIONS.find(o => o.symbol === val);
        if (match && 'index' in next) next.index = match.index;
      }

      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await onConfigSave(strategy.strategy_name, config);
    setSaving(false);
  };

  // Ordered field display: index/symbol/strike first, sl_mode next, then the rest.
  // Hide the half of SL fields that doesn't match the current mode.
  const slMode       = config.sl_mode || 'pct';
  const priorityKeys = ['index', 'symbol', 'strike_offset', 'otm_offset', 'sl_mode'];
  const allKeys      = Object.keys(config);
  const orderedKeys  = [
    ...priorityKeys.filter(k => allKeys.includes(k)),
    ...allKeys.filter(k => !priorityKeys.includes(k)),
  ].filter(k => {
    if (slMode === 'pct' && SL_PTS_FIELDS.has(k)) return false;   // hide pts fields in pct mode
    if (slMode === 'pts' && SL_PCT_FIELDS.has(k)) return false;   // hide pct fields in pts mode
    return true;
  });

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>
              {strategy.strategy_name.replace(/_/g, ' ').toUpperCase()}
            </span>
            <span className={`badge ${statusColor[strategy.status] || 'badge-grey'}`}>
              {strategy.status}
            </span>
            {strategy.paper_trading && <span className="badge badge-blue">PAPER</span>}
          </div>
          <p style={{ color: 'var(--muted)', fontSize: 12, margin: 0, maxWidth: 520 }}>
            {DESCRIPTIONS[strategy.strategy_name]}
          </p>
          {/* Quick config summary */}
          {!expanded && strategy.parameters?.index && (
            <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 99, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                {strategy.parameters.index}
              </span>
              {(() => {
                const offset = strategy.parameters.strike_offset ?? strategy.parameters.otm_offset ?? 0;
                const match  = STRIKE_OPTIONS.find(o => o.value === parseInt(offset, 10));
                return match ? (
                  <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 99, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--muted)' }}>
                    {match.label}
                  </span>
                ) : null;
              })()}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginLeft: 12 }}>
          <span style={{ color: (strategy.daily_pnl ?? 0) >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600, fontSize: 14 }}>
            {(strategy.daily_pnl ?? 0) >= 0 ? '+' : ''}₹{(strategy.daily_pnl ?? 0).toFixed(2)}
          </span>
          <label className="toggle">
            <input
              type="checkbox"
              checked={strategy.enabled}
              onChange={e => onToggle(strategy.strategy_name, e.target.checked)}
            />
            <span className="toggle-slider" />
          </label>
          <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setExpanded(x => !x)}>
            {expanded ? '▲ Config' : '▼ Config'}
          </button>
        </div>
      </div>

      {/* Config panel */}
      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 16 }}>
            {orderedKeys.map(key => (
              <ConfigField
                key={key}
                fieldKey={key}
                value={config[key]}
                onChange={handleFieldChange}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Config'}
            </button>
            <button className="btn btn-ghost" style={{ fontSize: 12 }}
              onClick={() => setConfig({ ...strategy.parameters })}>
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StrategyControl() {
  const [strategies, setStrategies] = useState([]);

  useEffect(() => { fetchStrategies(); }, []);

  const fetchStrategies = async () => {
    try {
      const res = await getStrategies();
      setStrategies(res.data.strategies || []);
    } catch {}
  };

  const handleToggle = async (name, enabled) => {
    try { await toggleStrategy(name, enabled); await fetchStrategies(); } catch {}
  };

  const handleConfigSave = async (name, config) => {
    try { await updateStrategyConfig(name, config); await fetchStrategies(); } catch {}
  };

  return (
    <div>
      <h1 className="page-title">⚙️ Strategies</h1>

      {strategies.length === 0 ? (
        <div className="card" style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>
          Strategy engine not running. Complete Fyers OAuth flow to start.
        </div>
      ) : (
        strategies.map(s => (
          <StrategyCard
            key={s.strategy_name}
            strategy={s}
            onToggle={handleToggle}
            onConfigSave={handleConfigSave}
          />
        ))
      )}
    </div>
  );
}
